import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { DCButtonLoader } from '@/components/ui/dc-loader';
import dcIcon from '@/assets/dc-icon.png';

interface SplashScreenProps {
  minDuration?: number;
  onFinish?: () => void;
}

/**
 * Animated splash screen for PWA launch.
 * Shows a coin logo with pulse animation and loading bar.
 */
export function SplashScreen({ minDuration = 1500, onFinish }: SplashScreenProps) {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Animate progress bar
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / minDuration) * 100, 100);
      setProgress(newProgress);

      if (newProgress >= 100) {
        clearInterval(interval);
        setIsExiting(true);
        setTimeout(() => {
          setIsVisible(false);
          onFinish?.();
        }, 400);
      }
    }, 16);

    return () => clearInterval(interval);
  }, [minDuration, onFinish]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[99999] flex flex-col items-center justify-center',
        'bg-background transition-all duration-400',
        isExiting && 'opacity-0 scale-105'
      )}
      style={{
        backgroundImage: `
          radial-gradient(ellipse 100% 60% at 50% -30%, hsla(225, 80%, 15%, 0.4), transparent),
          radial-gradient(ellipse 80% 50% at 100% 80%, hsla(270, 60%, 12%, 0.3), transparent),
          radial-gradient(ellipse 60% 40% at 0% 100%, hsla(210, 70%, 10%, 0.2), transparent)
        `
      }}
    >
      {/* Logo container with glow effect */}
      <div className="relative mb-8">
        {/* Glow rings */}
        <div className="absolute inset-0 animate-ping opacity-20">
          <div className="w-32 h-32 rounded-full bg-primary/30" />
        </div>
        <div 
          className="absolute -inset-4 rounded-full opacity-40 blur-xl"
          style={{
            background: 'linear-gradient(135deg, hsl(210 100% 55% / 0.3) 0%, hsl(270 85% 60% / 0.3) 100%)'
          }}
        />
        
        {/* Logo */}
        <div className={cn(
          'relative w-28 h-28 rounded-3xl flex items-center justify-center',
          'bg-card/80 backdrop-blur border border-border/50',
          'shadow-2xl',
          'animate-in zoom-in-50 duration-500'
        )}>
          <img
            src={dcIcon}
            alt="Drift Control"
            className="w-20 h-20 object-contain animate-pulse"
          />
        </div>
      </div>

      {/* App name */}
      <div className={cn(
        'mb-2',
        'animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200'
      )}>
        <h1
          className="m-0 text-2xl md:text-3xl font-extrabold tracking-[0.06em] uppercase bg-gradient-to-r from-[hsl(270,80%,60%)] via-[hsl(270,60%,45%)] to-[hsl(270,30%,15%)] bg-clip-text text-transparent"
          style={{ fontFamily: "'Inter', 'Montserrat', 'Segoe UI', system-ui, sans-serif", fontWeight: 900 }}
        >
          DRIFT CONTROL
        </h1>
      </div>
      
      <p className={cn(
        'text-sm text-muted-foreground mb-8',
        'animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300'
      )}>
        Gestão de risco para criptomoedas
      </p>

      {/* Progress bar */}
      <div className={cn(
        'w-48 h-1 rounded-full bg-muted/50 overflow-hidden',
        'animate-in fade-in duration-500 delay-400'
      )}>
        <div
          className="h-full rounded-full transition-all duration-100 ease-out"
          style={{
            width: `${progress}%`,
            background: 'linear-gradient(90deg, hsl(210 100% 55%), hsl(270 85% 60%))'
          }}
        />
      </div>

      {/* Mini loader instead of text */}
      <div className={cn(
        'mt-4 animate-in fade-in duration-500 delay-500'
      )}>
        <DCButtonLoader size={24} />
      </div>
    </div>
  );
}
